import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useParams } from 'react-router-dom';
import useFetchList from 'hooks/useFetchList';
import showServerError from 'helpers/showError';
import { useDispatch, useSelector } from 'react-redux';
import { NULL_QUERY_DELAY } from '../../Shared/constants';
import { CLAIM_REPORTS_COLUMNS } from '../constants';
import { ReactComponent as DownloadIcon } from 'assets/icons/file_download_black.svg';
import { useMutation } from 'react-fetching-library';
import QhsTable from '../../Shared/Table/Table';
import MenuItem from '../../Shared/Table/widgets/MenuItem';
import { convertDataFromApi, useGenerateColumns } from '../../Shared/Table/helpers';
import { TABLE_KEYS } from '../../../redux_store';

/*
  could be rendered in 2 cases:
  - all account Claim Reports
  - Claim Reports for a special user (patientUuid in URL params)
*/

const ClaimReportList = forwardRef(({ setFilterStyle, setTotalCount, tableTitle, q }, ref) => {
  const { accountUuid: paramAccountUuid } = useParams();
  const {
    en: { notifications, titles },
  } = useSelector(state => state.i18n);
  const dispatch = useDispatch();
  const [modifiedClaimReports, setModifiedClaimReports] = useState();
  const [prevTotalCount, setPrevTotalCount] = useState(0);
  const {
    user,
    isDistributionSponsor,
    metadata: { currentAccount },
  } = useSelector(state => state.user);
  const tableKey = isDistributionSponsor
    ? TABLE_KEYS.claimReportsDSTable
    : TABLE_KEYS.claimReportsTable;
  const columns = useGenerateColumns(CLAIM_REPORTS_COLUMNS, tableKey, {}, {}).columns;
  const fields = columns.map(columnData => columnData.accessorKey).sort().join();

  const baseEndpoint = `/accounts/${paramAccountUuid || currentAccount.uuid}/claimReports`;
  const {
    items,
    totalCount,
    reloadData,
    clearFilter,
    sort,
    onSort,
    filter,
    onFilter,
    loading: dataLoading,
    onLoadMoreRows,
    fetchError,
  } = useFetchList({
    baseEndpoint,
    baseParams: {
      fields: fields,
      q: q || undefined,
    },
    tableKey,
    canLoad: fields.length,
  });

  useEffect(() => {
    if (items) {
      const newModifiedItems = convertDataFromApi(items, tableKey, filter);
      setModifiedClaimReports(newModifiedItems);
    }
  }, [items]);

  useEffect(() => {
    if (prevTotalCount !== totalCount) {
      setFilterStyle &&
        setFilterStyle(
          !!filter?.reduce((accumulator, { value }) => {
            return accumulator + value.toString().replaceAll(' ', '').length;
          }, 0)
        );
      setPrevTotalCount(totalCount);
      setTotalCount && setTotalCount(totalCount);
    }
  }, [filter, totalCount]);

  const reloadClaimReportList = hasFilter => {
    if (!hasFilter) {
      clearFilter();
    } else {
      reloadData({ delay: NULL_QUERY_DELAY });
    }
  };

  useImperativeHandle(ref, () => ({
    reloadList(v) {
      reloadClaimReportList(v);
    },
  }));

  const getReportPDF = link => ({
    method: 'GET',
    endpoint: link,
    responseType: 'blob',
    credentials: 'include',
  });

  const { mutate: getPDFFile, loading: isPDFLoading } = useMutation(getReportPDF);

  const downloadPDF = async (link, claimReport, setAnchorEl) => {
    const { payload, error, status } = await getPDFFile(link);
    if (error) {
      const options = payload && {
        correlationUUID: payload.correlationUUID,
        userUUID: user.uuid,
      };
      switch (status) {
        case 401: {
          showServerError(dispatch, notifications.notPermissionForDownloadingFile);
          break;
        }
        default: {
          showServerError(dispatch, '', options);
          break;
        }
      }
    } else if (payload && payload.size > 0) {
      const url = window.URL.createObjectURL(
        new Blob([payload], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;',
        })
      );
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `Claim Report | Claim Report Generated Timestamp: ${claimReport.claimReportGeneratedTimestamp}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    }
    setAnchorEl && setAnchorEl(null);
  };

  const downloadReportPDF = async (e, claimReport, setAnchorEl) => {
    const link = `/accounts/${claimReport.accountUUID}/claimReports/${claimReport.uuid}/report`;
    await downloadPDF(link, claimReport, setAnchorEl);
  };

  return (
    <QhsTable
      columns={columns}
      data={modifiedClaimReports || []}
      fetchNextPage={onLoadMoreRows}
      isFetching={dataLoading}
      isLoading={dataLoading && !modifiedClaimReports}
      totalRowCount={totalCount}
      sorting={sort}
      setSorting={onSort}
      filter={filter}
      setFilters={onFilter}
      tableKey={tableKey}
      tableTitle={tableTitle}
      showRefreshTableButton
      reloadData={reloadClaimReportList}
      fetchError={fetchError}
      renderRowActionMenuItems={({ row, setAnchorEl }) => (
        <MenuItem
          title={titles.downloadReport}
          setAnchorEl={() => {}}
          isLoading={isPDFLoading}
          icon={<DownloadIcon width={16} height={16} />}
          onClick={e => downloadReportPDF(e, row.original, setAnchorEl)}
        />
      )}
    />
  );
});

export default ClaimReportList;
